/**
 * Trang chủ công khai — hiển thị danh sách BĐS nổi bật hoặc kết quả tìm kiếm.
 *
 * Luồng dữ liệu:
 * - Đọc query string từ URL (useSearchParams) để biết đang tìm kiếm hay xem trang chủ.
 * - Chế độ tìm kiếm: render HomeSearchResults (hook usePropertyList bên trong).
 * - Chế độ mặc định: useFeaturedProperties lấy các section listing, có thể lọc theo home_category.
 * - Quản lý scroll: lưu/khôi phục vị trí cuộn khi chuyển đổi giữa tìm kiếm và trang chủ.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InlineLoadingIndicator } from '../../components/Common/InlineLoadingIndicator';
import { ListingRow } from '../../components/home/ListingRow';
import { HomeSearchResults } from '../../components/home/HomeSearchResults';
import { useFeaturedProperties } from '../../features/properties/hooks/useFeaturedProperties';
import {
  HOME_CATEGORY_PARAM,
  hasActivePropertySearch,
} from '../../shared/utils/propertySearchParams';
import { restorePageScroll, scrollToPageTop } from '../../shared/utils/scrollToPageTop';
import { UI_MIN_DELAY_MS } from '../../shared/utils/waitMinimumDelay';
import { TEXT } from '../../shared/languages';

/** Thời gian tối thiểu hiển thị loading khi quay lại chế độ trang chủ */
const HOME_VIEW_LOADING_MIN_MS = UI_MIN_DELAY_MS;

export const HomePage = () => {
  const [searchParams] = useSearchParams();
  /** Chuỗi query đầy đủ — dùng làm dependency để phát hiện thay đổi bộ lọc */
  const searchKey = searchParams.toString();
  const { listingSections, isLoadingHomeData } = useFeaturedProperties();
  const t = TEXT.home;
  /** true khi URL có tham số tìm kiếm hoạt động → chuyển sang HomeSearchResults */
  const isSearching = hasActivePropertySearch(searchParams);
  /** ID danh mục lọc trên trang chủ (khác category_id của chế độ tìm kiếm) */
  const homeCategoryId = searchParams.get(HOME_CATEGORY_PARAM);

  /** Lọc properties trong từng section theo danh mục đã chọn trên thanh category */
  const visibleSections = useMemo(() => {
    if (!homeCategoryId) {
      return listingSections;
    }

    return listingSections
      .map((section) => ({
        ...section,
        properties: section.properties.filter(
          (property) => String(property.category?.id) === homeCategoryId
        ),
      }))
      .filter((section) => section.properties.length > 0);
  }, [homeCategoryId, listingSections]);

  /** Trạng thái chuyển tiếp khi rời chế độ tìm kiếm, tránh flash nội dung cũ */
  const [isReturningToAll, setIsReturningToAll] = useState(false);
  /** Theo dõi trạng thái tìm kiếm trước đó để xử lý scroll */
  const wasSearchingRef = useRef(isSearching);
  /** Lưu vị trí cuộn trang chủ trước khi vào tìm kiếm */
  const savedFeaturedScrollRef = useRef(0);
  /** Cờ báo cần khôi phục scroll sau khi dữ liệu featured đã sẵn sàng */
  const shouldRestoreFeaturedScrollRef = useRef(false);

  const showFeaturedLoading = !isSearching && (isLoadingHomeData || isReturningToAll);

  /** Cuộn lên đầu trang khi mount lần đầu */
  useEffect(() => {
    scrollToPageTop();
  }, []);

  /** Đồng bộ vị trí cuộn vào ref khi đang ở chế độ trang chủ (không tìm kiếm) */
  useEffect(() => {
    if (isSearching) {
      return undefined;
    }

    const syncFeaturedScroll = () => {
      savedFeaturedScrollRef.current = window.scrollY;
    };

    syncFeaturedScroll();
    window.addEventListener('scroll', syncFeaturedScroll, { passive: true });

    return () => {
      syncFeaturedScroll();
      window.removeEventListener('scroll', syncFeaturedScroll);
    };
  }, [isSearching]);

  /**
   * Xử lý chuyển đổi giữa chế độ tìm kiếm và trang chủ:
   * - Vào tìm kiếm: cuộn lên đầu.
   * - Thoát tìm kiếm: bật loading tạm và đánh dấu cần khôi phục scroll.
   * - Đổi bộ lọc trong lúc đang tìm: cuộn lên đầu.
   */
  useEffect(() => {
    const wasSearching = wasSearchingRef.current;

    if (!wasSearching && isSearching) {
      scrollToPageTop();
    } else if (wasSearching && !isSearching) {
      shouldRestoreFeaturedScrollRef.current = true;
      setIsReturningToAll(true);

      const timer = window.setTimeout(() => {
        setIsReturningToAll(false);
      }, HOME_VIEW_LOADING_MIN_MS);

      wasSearchingRef.current = isSearching;

      return () => window.clearTimeout(timer);
    } else if (wasSearching && isSearching) {
      scrollToPageTop();
    }

    wasSearchingRef.current = isSearching;
    return undefined;
  }, [searchKey, isSearching]);

  /** Khôi phục vị trí cuộn đã lưu sau khi featured content render xong */
  useEffect(() => {
    if (isSearching || !shouldRestoreFeaturedScrollRef.current || showFeaturedLoading) {
      return;
    }

    shouldRestoreFeaturedScrollRef.current = false;
    restorePageScroll(savedFeaturedScrollRef.current);
  }, [isSearching, showFeaturedLoading, visibleSections.length]);

  if (isSearching) {
    return (
      <div className="home-page home-page--search">
        <HomeSearchResults />
      </div>
    );
  }

  return (
    <div className="home-page home-page--featured">
      {showFeaturedLoading && (
        <InlineLoadingIndicator overlay label={TEXT.messages.searchingProperties} />
      )}

      <div className="home-page-container">
        {isLoadingHomeData ? (
          <>
            <ListingRow title={t.sectionSale} isLoading />
            <ListingRow title={t.sectionPopular} isLoading />
          </>
        ) : visibleSections.length ? (
          visibleSections.map((section) => (
            <ListingRow
              key={section.id}
              title={section.title}
              href={section.href}
              properties={section.properties}
            />
          ))
        ) : (
          <div className="home-empty">
            <h2>{t.emptySectionTitle}</h2>
            <p>{t.emptySectionMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};
